import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FiSearch } from "react-icons/fi";
import { EmptyState } from "../../../../common/comms/EmptyState";
import { RetryBanner } from "../../../../common/comms/RetryBanner";
import { SkeletonRows } from "../../../../common/comms/SkeletonRows";
import { useCommsIdentity } from "../../../../hooks/comms/useCommsIdentity";
import { ClientTableRow } from "./ClientTableRow";
import {
  ALL_CLIENT_FILTER_IDS,
  CLIENT_FILTER_DEFS,
  CLIENT_SORT_IDS,
  CLIENT_SORT_OPTIONS,
  countClientFilterMatches,
  matchesClientFilters,
  searchClients,
  sortClients,
  toggleClientFilter,
} from "./clientRosterSort";

/**
 * One filter chip. Active chips take the brand tint; the count is always shown
 * so an empty chip reads as "none match" rather than looking broken.
 */
function FilterChip({ label, count, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`min-h-[36px] inline-flex items-center gap-1 px-3 rounded-full text-xs font-medium border transition-colors ${
        active
          ? "bg-light-orange text-orange-primary border-orange-200"
          : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50"
      }`}
    >
      {label}
      <span className="opacity-70">({count})</span>
    </button>
  );
}

/**
 * A coach's client roster (`GET /oversight/coaches/{coach_id}/clients`), with
 * search, filter chips and sort.
 *
 * The endpoint returns the whole roster unpaginated, so client-side filtering
 * and sorting is honest here — every count is over the full list, not over a
 * loaded page.
 *
 * Defaults to "most neglected first", which is the question this tab exists to
 * answer. Filters are the ones the endpoint can actually support today; the
 * design's plan-based chips need per-coach insights the backend cannot scope
 * yet, and are absent rather than approximated.
 *
 * Follows the oversight API error contract: 403 means "outside your report
 * chain"; 502/504/network/other render a RetryBanner; an empty roster is an
 * EmptyState, never an error.
 *
 * Presentational: the roster is fetched once by `useCoachClients` in the page
 * shell and shared with the Overview tab, so the two can never disagree.
 *
 * @param {Object} props
 * @param {Array<Object>} props.clients - Roster rows.
 * @param {boolean} props.loading
 * @param {'forbidden'|'gateway'|'other'|null} props.errorKind
 * @param {() => void} props.onRetry
 */
export function ClientsTab({ clients, loading, errorKind, onRetry }) {
  const navigate = useNavigate();
  const { getClientName } = useCommsIdentity();

  const [search, setSearch] = useState("");
  const [selectedFilters, setSelectedFilters] = useState([]);
  const [sortId, setSortId] = useState(CLIENT_SORT_IDS.NEGLECTED);

  const searched = useMemo(
    () => searchClients(clients, search, getClientName),
    [clients, search, getClientName]
  );

  // Counted against the searched list but not against the other chips, so a
  // chip never reads zero merely because a different chip is active.
  const counts = useMemo(() => countClientFilterMatches(searched), [searched]);

  const visible = useMemo(() => {
    const filtered = searched.filter((c) => matchesClientFilters(c, selectedFilters));
    return sortClients(filtered, sortId, { nameOf: getClientName });
  }, [searched, selectedFilters, sortId, getClientName]);

  const handleOpenRoom = (roomId) => navigate(`/oversight/${roomId}`);
  const handleToggleFilter = (id) => setSelectedFilters((cur) => toggleClientFilter(cur, id));

  if (loading) {
    return (
      <div className="rounded-xl border border-gray-200 bg-white p-4">
        <SkeletonRows variant="rows" count={5} />
      </div>
    );
  }

  if (errorKind === "forbidden") {
    return (
      <EmptyState
        title="This coach is outside your report chain"
        hint="You don't have access to this coach's clients."
      />
    );
  }

  if (errorKind === "gateway" || errorKind === "other") {
    return (
      <div className="rounded-xl border border-gray-200 bg-white p-4">
        <RetryBanner
          message={
            errorKind === "gateway"
              ? "Something went wrong while loading."
              : "Something went wrong loading this coach's clients."
          }
          onRetry={onRetry}
        />
      </div>
    );
  }

  if (clients.length === 0) {
    return <EmptyState title="No active clients" />;
  }

  const isFiltered = selectedFilters.length > 0 || search.trim().length > 0;

  return (
    <div className="space-y-3">
      <div className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-2.5">
        <h2 className="text-base font-bold text-gray-900">
          Their {clients.length} client{clients.length === 1 ? "" : "s"}
        </h2>
        <span className="text-xs text-gray-500">
          Who they are and where the coaching has got to
        </span>
      </div>

      <div className="flex flex-col lg:flex-row lg:items-center gap-2">
        <div className="relative flex-1 min-w-0">
          <FiSearch
            className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
            aria-hidden="true"
          />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search client"
            aria-label="Search client"
            className="w-full min-h-[44px] pl-9 pr-3 rounded-lg border border-gray-200 bg-white text-sm text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-primary/30"
          />
        </div>

        <label className="flex items-center gap-2 text-xs text-gray-500 flex-shrink-0">
          <span className="whitespace-nowrap">Sort by</span>
          <select
            value={sortId}
            onChange={(e) => setSortId(e.target.value)}
            className="min-h-[44px] px-3 rounded-lg border border-gray-200 bg-white text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-orange-primary/30"
          >
            {CLIENT_SORT_OPTIONS.map((o) => (
              <option key={o.id} value={o.id}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="flex flex-wrap gap-2">
        <FilterChip
          label="All clients"
          count={searched.length}
          active={selectedFilters.length === 0}
          onClick={() => setSelectedFilters([])}
        />
        {ALL_CLIENT_FILTER_IDS.map((id) => (
          <FilterChip
            key={id}
            label={CLIENT_FILTER_DEFS[id].label}
            count={counts[id] ?? 0}
            active={selectedFilters.includes(id)}
            onClick={() => handleToggleFilter(id)}
          />
        ))}
      </div>

      {visible.length === 0 ? (
        <EmptyState
          title="No clients match"
          hint="Clear a filter or change the search to widen the list."
        />
      ) : (
        <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
          {visible.map((client) => (
            <ClientTableRow
              key={client.client_id}
              client={client}
              onOpenRoom={handleOpenRoom}
            />
          ))}
        </div>
      )}

      <p className="text-[11px] text-gray-400">
        {isFiltered
          ? `Showing ${visible.length} of ${clients.length}.`
          : `All ${clients.length} clients.`}{" "}
        "Gone quiet" and "most neglected" measure silence from the coach, not room activity. Diet
        and workout plan ages need per-coach insights, which the backend cannot scope to another
        coach yet.
      </p>
    </div>
  );
}

export default ClientsTab;
